import { useEffect, useRef } from 'react';

import { logException } from '@paradigm/logging/src/logging';
import {
  useTradeTapeIdsList,
  useTradeTapeService,
} from '@paradigm/features/src/unified-rfqs/contexts/unified-trade-tapes';
import { useTerminalAgent } from '@tradeparadigm/terminal-react';

import { buildBlockTradeContext } from '#/features/terminal/block-trade-context';

const DEBOUNCE_MS = 500;

/** True when a request rejection means the gateway has no such method. */
function isUnknownMethod(err: unknown): boolean {
  const msg = err instanceof Error ? err.message : String(err);
  return msg.toLowerCase().includes('unknown method');
}

/**
 * Pumps the visible UnifiedRfqs trade-tape into the AI agent as a structured
 * `set_block_trade_context` gateway request, giving the agent live tape
 * context for general "what's on the tape" questions.
 *
 * NOTE: not every gateway implements `set_block_trade_context` (e.g. a
 * standalone Dime/OpenClaw gateway rejects it as "unknown method"). When that
 * happens we stop sending after the first rejection so we don't error on every
 * tape tick — `/analyze` doesn't rely on this pump anyway; it carries its
 * trade data inline as hidden message context.
 *
 * Rendered through `<Terminal agentEffects>` so it can reach the agent via
 * `useTerminalAgent()` while living entirely in host code. Renders nothing.
 */
export default function BlockTradeContextPump() {
  const agent = useTerminalAgent();
  const tradeIds = useTradeTapeIdsList();
  const service = useTradeTapeService();

  const isAuthenticated = agent.status === 'authenticated';
  const tradeIdsRef = useRef(tradeIds);
  tradeIdsRef.current = tradeIds;

  // `request` is context-stable; mirror it into a ref so the unmount
  // cleanup below doesn't need it as an effect dependency.
  const requestRef = useRef(agent.request);
  requestRef.current = agent.request;

  // Set once the gateway rejects the method as unknown; gates further sends.
  const unsupportedRef = useRef(false);

  useEffect(() => {
    if (!isAuthenticated || unsupportedRef.current) return undefined;

    const handle = setTimeout(() => {
      requestRef
        .current(
          'set_block_trade_context',
          buildBlockTradeContext(tradeIdsRef.current, service),
        )
        .catch((err: unknown) => {
          if (isUnknownMethod(err)) {
            // Gateway doesn't support it — stop pumping (and skip the unmount
            // clear below) rather than erroring on every tape change.
            unsupportedRef.current = true;
            return;
          }
          logException('terminal:set_block_trade_context', err);
        });
    }, DEBOUNCE_MS);

    return () => clearTimeout(handle);
  }, [tradeIds, service, isAuthenticated]);

  // Clear context on unmount (closing the tab / disconnecting) — unless the
  // gateway never accepted it in the first place.
  useEffect(() => {
    return () => {
      if (unsupportedRef.current) return;
      requestRef.current('clear_block_trade_context', {}).catch(() => {
        // Best-effort cleanup; ignore failures.
      });
    };
  }, []);

  return null;
}
